import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { AudioPlayerProvider } from '../../context/audio-player-context'; // Import Audio Provider
import { useSync } from '../../store/sync-context';
import GlobalSearchOverlay from '../features/search/global-search-overlay'; // Import search overlay
import InstallPrompt from '../install-prompt';
import BottomNav from '../navigation/bottom-nav';
import TopNavbar from '../navigation/top-navbar';
import { GlobalAudioPlayer } from './global-audio-player'; // Import Global Player

interface MainLayoutProps {
    children?: React.ReactNode;
}

/**
 * MainLayout component provides the overall structure for the PWA
 * - Responsive layout that adapts to different screen sizes
 * - Contains the top navigation bar and bottom mobile navigation
 * - Shows online/offline status indicators
 * - Includes the PWA install prompt when applicable
 */
const MainLayoutContent: React.FC<MainLayoutProps> = ({ children }) => {
    const location = useLocation();
    const { isOnline, pendingActions } = useSync();
    const [scrolled, setScrolled] = useState(false);

    // Handle scroll events to apply styling to the header when scrolled
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="pt-safe min-h-screen bg-neutral-50 text-neutral-900 dark:bg-neutral-900 dark:text-white">
            {/* Top navigation bar */}
            <TopNavbar scrolled={scrolled} isOnline={isOnline} hasPendingActions={pendingActions.length > 0} />

            {/* Connection status indicator */}
            <AnimatePresence>
                {!isOnline && (
                    <motion.div
                        className="fixed inset-x-0 top-16 z-50 flex items-center justify-center gap-2 bg-amber-500 py-2 text-white shadow-md"
                        initial={{ y: -100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -100, opacity: 0 }}
                        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                    >
                        <span className="h-2 w-2 animate-pulse rounded-full bg-white"></span>
                        <span className="text-sm font-medium">You are offline</span>
                        {pendingActions.length > 0 && (
                            <motion.span
                                className="ml-2 rounded-full bg-white px-1.5 py-0.5 text-xs font-medium text-amber-600"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                            >
                                {pendingActions.length}
                            </motion.span>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main content area with extra padding for offline status and bottom bars */}
            {/* Mobile: pb-32 (8rem = 128px) to account for BottomNav (h-16) + GlobalAudioPlayer (h-16) */}
            {/* Desktop: md:pb-10 (2.5rem = 40px) as BottomNav is hidden */}
            <main className={`py-4 px-2 pb-32 transition-all sm:px-6 ${!isOnline ? 'pt-24' : 'pt-20'} md:pb-10`}>
                <div className="mx-auto max-w-7xl">
                    <motion.div
                        key={location.pathname}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        {children || <Outlet />} {/* Support both children and Outlet for flexibility */}
                    </motion.div>
                </div>
            </main>

            {/* Install prompt */}
            <InstallPrompt className="fixed inset-x-0 bottom-20 mx-4 md:mx-auto md:max-w-md" />

            {/* Bottom navigation for mobile - always visible */}
            <BottomNav />

            {/* Global Search Overlay */}
            <GlobalSearchOverlay />

            {/* Global Audio Player */}
            <GlobalAudioPlayer />
        </div>
    );
};

// Wrap the layout content with the provider
const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    return (
        <AudioPlayerProvider>
            <MainLayoutContent>{children}</MainLayoutContent>
        </AudioPlayerProvider>
    );
};


export default MainLayout;
